<template>
  <Dialog :open="open" @update:open="(value) => emit('update:open', value)">
    <DialogContent class="sm:max-w-lg" data-test-id="transfer-ownership-dialog">
      <DialogHeader>
        <DialogTitle>{{ t('admin.setup.team.transfer.title') }}</DialogTitle>
        <DialogDescription>{{ t('admin.setup.team.transfer.description') }}</DialogDescription>
      </DialogHeader>

      <form class="flex flex-col gap-5" novalidate @submit.prevent="submit">
        <!-- Liste bornée : la fenêtre reste utilisable avec beaucoup de super admins. -->
        <fieldset class="flex max-h-72 flex-col gap-2 overflow-y-auto p-0.5">
          <legend class="sr-only">{{ t('admin.setup.team.transfer.candidates') }}</legend>
          <label
            v-for="admin in candidatesRef"
            :key="admin.id"
            class="flex cursor-pointer items-center gap-3 rounded-lg border-2 p-3"
            :class="selectedRef === admin.id ? 'border-primary' : 'border-border'"
            :data-test-id="`transfer-ownership-candidate-${admin.email}`"
          >
            <input v-model="selectedRef" type="radio" name="new-owner" :value="admin.id" class="accent-primary" />
            <span class="flex min-w-0 flex-col">
              <span class="truncate text-sm font-semibold">{{ displayName(admin) }}</span>
              <span class="truncate text-sm text-muted-foreground">{{ admin.email }}</span>
            </span>
          </label>
          <p v-if="!candidatesRef.length" class="text-sm text-muted-foreground" data-test-id="transfer-ownership-empty">
            {{ t('admin.setup.team.transfer.empty') }}
          </p>
          <p v-else-if="hasPendingRef" class="text-sm text-muted-foreground">
            {{ t('admin.setup.team.transfer.pendingHint') }}
          </p>
        </fieldset>

        <div v-if="candidatesRef.length" class="flex flex-col gap-2">
          <Label for="transfer-ownership-confirm">
            {{ t('admin.setup.team.transfer.confirmLabel', { word: confirmWordRef }) }}
          </Label>
          <Input
            id="transfer-ownership-confirm"
            v-model="confirmRef"
            autocomplete="off"
            data-test-id="transfer-ownership-confirm-input"
          />
        </div>

        <p v-if="errorRef" role="alert" class="text-sm text-destructive">{{ errorRef }}</p>

        <DialogFooter class="gap-2">
          <Button type="button" variant="ghost" @click="emit('update:open', false)">
            {{ t('admin.setup.team.remove.cancel') }}
          </Button>
          <Button type="submit" :disabled="!canSubmitRef || isTransferringRef" data-test-id="transfer-ownership-submit">
            <Spinner v-if="isTransferringRef" aria-hidden="true" />
            {{
              selectedAdminRef
                ? t('admin.setup.team.transfer.submitTo', { name: displayName(selectedAdminRef) })
                : t('admin.setup.team.transfer.submit')
            }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { toast } from 'vue-sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import type { SuperAdminType } from '@/features/admin/platform-setup/api'
import useSuperAdmins from '@/features/admin/platform-setup/hooks/use-super-admins.hook'
import { useI18n } from '@/lib/i18n'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ 'update:open': [open: boolean] }>()

const { t } = useI18n()
const { superAdminsRef, transfer, isTransferringRef } = useSuperAdmins()

const selectedRef = ref<string | null>(null)
const confirmRef = ref('')
const errorRef = ref('')

// Seuls les super admins actifs, hors équipe Constel, peuvent devenir propriétaires.
const candidatesRef = computed(() =>
  superAdminsRef.value.filter((admin) => !admin.isOwner && !admin.pending && !admin.isConstelStaff),
)
const hasPendingRef = computed(() => superAdminsRef.value.some((admin) => admin.pending))
const selectedAdminRef = computed(() => candidatesRef.value.find((admin) => admin.id === selectedRef.value) ?? null)

// Mot à recopier : accents et casse ignorés (« transferer » passe).
const normalize = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toUpperCase()
const confirmWordRef = computed(() => t('admin.setup.team.transfer.confirmWord'))
const canSubmitRef = computed(
  () => !!selectedAdminRef.value && normalize(confirmRef.value) === normalize(confirmWordRef.value),
)

const displayName = (admin: SuperAdminType) =>
  [admin.firstname, admin.lastname].filter(Boolean).join(' ') || admin.email

watch(
  () => props.open,
  (open) => {
    if (!open) return
    selectedRef.value = candidatesRef.value.length === 1 ? candidatesRef.value[0].id : null
    confirmRef.value = ''
    errorRef.value = ''
  },
)

async function submit() {
  if (!canSubmitRef.value || !selectedAdminRef.value) return
  errorRef.value = ''
  try {
    const name = displayName(selectedAdminRef.value)
    await transfer(selectedAdminRef.value.id)
    toast.success(t('admin.setup.team.transfer.done', { name }))
    emit('update:open', false)
  } catch {
    errorRef.value = t('admin.setup.team.transfer.error')
  }
}
</script>
